import {FieldType} from '@ngx-formly/core';
import {Component, ElementRef, Renderer2, ViewChild} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {NewsOverviewService} from '@features/news/services/news-overview.service';
import {EditorComponent} from "@capturum/ui/editor";
import {AfterViewInit, NgZone, ChangeDetectorRef} from '@angular/core';

@Component({

    template: `
        <div class="editor-wrapper" [class.uploading]="isUploading">
            <cap-editor *ngIf="!formControl?.disabled && isNewsEditor" [formControl]="formControl" [config]="config"
                        [formlyAttributes]="field"></cap-editor>
            <cap-editor *ngIf="!formControl?.disabled && !isNewsEditor" [formControl]="formControl"
                        [formlyAttributes]="field"></cap-editor>

            <div class="readonly-editor" *ngIf="formControl?.disabled"
                 [innerHtml]="this.sanitizer.bypassSecurityTrustHtml(formControl.value)">
            </div>

            <div *ngIf="isUploading" class="spinner-overlay">
                <app-loader></app-loader>
            </div>
        </div>
    `,
    styleUrls: ['./editor.formly-type.scss']
})
export class EditorFormlyTypeComponent extends FieldType implements AfterViewInit {
    @ViewChild(EditorComponent) capEditor: EditorComponent;

    public sanitizer: DomSanitizer;
    public apiHttp: NewsOverviewService;
    public isUploading: boolean = false;
    public isNewsEditor: boolean = window.location.href.indexOf('communication/news') > -1;
    public config = {
        modules: {
            toolbar: {
                container: [
                    ['bold', 'italic', 'underline', 'strike'],
                    [{'align': []}],
                    [{'list': 'ordered'}, {'list': 'bullet'}],
                    [{'indent': '-1'}, {'indent': '+1'}],
                    ['link', 'image', 'video'],
                    ['clean']
                ],
                handlers: {
                    image: () => this.imageHandler()
                }
            },
            history: {
                delay: 2000,
                maxStack: 500,
                userOnly: true
            },
        }
    };

    constructor(sanitizer: DomSanitizer,
                apiHttp: NewsOverviewService,
                private renderer: Renderer2,
                private elRef: ElementRef,
                private ngZone: NgZone,
                private cdr: ChangeDetectorRef
    ) {
        super();
        this.sanitizer = sanitizer;
        this.apiHttp = apiHttp;
    }


    public ngAfterViewInit() {
        const nativeEl = this.elRef.nativeElement;

        this.renderer.listen(nativeEl, 'dragover', e => e.preventDefault());
        this.renderer.listen(nativeEl, 'drop', this.handleDrop.bind(this));
        this.renderer.listen(nativeEl, 'paste', this.handlePaste.bind(this));
    }


    public handleDrop(evt: DragEvent) {
        evt.preventDefault();

        if (evt.dataTransfer && evt.dataTransfer.files && evt.dataTransfer.files.length > 0) {
            const file = evt.dataTransfer.files[0];
            this.ngZone.run(() => {
                this.isUploading = true;
                this.cdr.detectChanges();
                this.uploadAndInsertImage(file);
            });
        }
    }

    public handlePaste(evt: ClipboardEvent) {
        if (!evt.clipboardData) {
            return;
        }
        const items = evt.clipboardData.items;

        if (!items) { return; }

        let hasImage = false;

        for (let i = items.length - 1; i >= 0; i--) {

            if (items[i].type.indexOf('image') !== -1) {
                hasImage = true;
                const file = items[i].getAsFile();

                if (file) {
                    this.ngZone.run(() => {
                        this.isUploading = true;
                        this.cdr.detectChanges();
                        this.uploadAndInsertImage(file);
                    });
                    evt.preventDefault();
                }

                break;
            }
        }

        if (hasImage) {
            evt.preventDefault();
        }
    }

    public async uploadAndInsertImage(file: File) {
        try {
            const uniqueFile = this.generateUniqueFileName(file);
            const resizedFile = await this.resizeImageFile(uniqueFile, 800, 800);

            this.apiHttp.uploadImageFile(resizedFile).subscribe({
                next: (response) => {
                    const quill = this.getQuillEditorInstance();

                    if (!quill) {
                        this.isUploading = false;
                        return;
                    }

                    const editorRoot = quill.root;

                    // Remove base64 src images from the editor
                    const imgs = editorRoot.querySelectorAll('img');

                    imgs.forEach(img => {
                        const src = img.getAttribute('src');
                        if (src?.startsWith('data:image/')) {
                            img.remove();
                        }
                    });

                    const range = quill.getSelection(true);

                    quill.insertEmbed(range.index, 'image', response.filePath);
                    quill.setSelection(range.index + 1);
                    this.formControl.setValue(quill.root.innerHTML);
                    this.isUploading = false;
                },
                error: () => {
                    alert('Image upload failed');
                    this.isUploading = false;
                }
            });
        } catch (error) {
            alert('Image processing failed');
            this.isUploading = false;
        }
    }

    public resizeImageFile(file: File, maxWidth: number, maxHeight: number): Promise<File> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const reader = new FileReader();

            reader.onload = e => {
                img.src = e.target.result as string;
            };
            reader.onerror = err => reject(err);

            img.onload = () => {
                let {width, height} = img;

                if (width <= maxWidth && height <= maxHeight) {
                    resolve(file);
                    return;
                }

                if (width > height) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                } else {
                    width = Math.round((width * maxHeight) / height);
                    height = maxHeight;
                }

                const canvas = document.createElement('canvas');

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');

                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob(blob => {
                    if (blob) {
                        const newFile = new File([blob], file.name, {type: file.type});
                        resolve(newFile);
                    } else {
                        reject(new Error('Canvas is empty'));
                    }
                }, file.type || 'image/jpeg', 0.8);
            };

            reader.readAsDataURL(file);
        });
    }

    public getQuillEditorInstance() {
        return this.capEditor?.quill?.quillEditor;
    }

    private generateUniqueFileName(file: File): File {
        const timestamp = Date.now();
        // Extract extension (if any)
        const origName = file.name || 'new-image.png';
        const dotIndex = origName.lastIndexOf('.');
        const ext = dotIndex > -1 ? origName.substring(dotIndex) : '.png';
        const baseName = dotIndex > -1 ? origName.substring(0, dotIndex) : origName;

        // Create new filename with timestamp
        const newName = `${baseName}-${timestamp}${ext}`;

        // Create a new File with the new name but same content/type
        return new File([file], newName, { type: file.type });
    }

    private imageHandler() {
        const input = document.createElement('input');

        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();
        input.onchange = () => {
            const file = input.files?.[0];

            if (file) {
                this.ngZone.run(() => {
                    this.isUploading = true;
                    this.cdr.detectChanges();
                    this.uploadAndInsertImage(file);
                });
            }
        };
    }
}
